import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Button from "../../base/button/button";
import DropZone from "../../base/dropzone";
import Utils, {createUUId} from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import {ReactComponent as ImagePlaceholder} from "../../../../assets/images/dropzone/upload-placeholder.svg";


const UploadBizImportFileDialog = ({
                                       open,
                                       close,
                                       onSelect,
                                       title,
                                       description,
                                       parse,
                                       validate,
                                   }) => {
    const idRef = useRef(createUUId(true));
    const [parseError, setParseError] = useState(null);
    const [parsing, setParsing] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Changes the id of the dropzone when the dialog is closed.
     */
    useEffect(() => {
        if (!open) {
            idRef.current = createUUId(true);
            setParseError(null);
            setParsing(false);
        }
    }, [open]);

    /**
     * With each change in the parseError state:
     * - if set, removes the error after 2 second timeout.
     */
    useEffect(() => {
        if (!parseError)
            return;
        const timer = setTimeout(() => {
            setParseError(null);
        }, 5000)
        return () => clearTimeout(timer);
    }, [parseError])

    /**
     * Validates the parses the given BizImport file.
     * @param {File} file
     */
    const validateAndParseFile = useCallback(async (file) => {
        try {
            let content = await Utils.importBizImportFile(file);
            if (parse) {
                content = await parse(content);
            }
            if (validate) {
                await validate(content);
            }
            return content;
        } catch (e) {
            setParseError(e.message);
        }
    }, [parse, validate]);


    /**
     * Validates and parses the provided csv file.
     *
     * * if the file is parsed properly, calls the onParsed callback with the parsed file as its arg.
     * @param files {File[] | null}
     */
    const onFileSelected = useCallback(async (files) => {
        setParseError(null);
        const file = files[0];
        setParsing(true);
        const parsed = await validateAndParseFile(file);
        if (!isMounted())
            return;
        setParsing(false);
        if (parsed) {
            onSelect(parsed);
            close();
        }
    }, [validateAndParseFile, isMounted, onSelect, close]);

    return (
        <>
            <Dialog
                open={open ?? false}
                setOpen={close}
                maxWidth={'sm'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    {title}
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText gutterBottom>
                        {description}
                    </DialogContentText>
                    <div className={'p-3'}>
                        <DropZone
                            disabled={parsing}
                            identifier={'import-products-fast-connection'}
                            accept={['.bizimport']}
                            id={idRef.current}
                            setLoading={() => (void 0)}
                            onSuccess={onFileSelected}
                            error={!!parseError}
                            multiple={false}
                            openOnClick
                            noParse
                        >
                            {() =>
                                <div className={'import-products-fast-connection-content'}>
                                    <p className={'placeholder-text'}>
                                        Upload your Biz Import file here. please ensure that the file has a .bizimport extension.
                                    </p>
                                    <ImagePlaceholder className={'placeholder-image'}/>
                                    {
                                        parseError &&
                                        <p className={'placeholder-text'}>
                                            {parseError}
                                        </p>
                                    }

                                </div>
                            }
                        </DropZone>
                    </div>
                </DialogContent>
                <DialogActions className={'p-0 m-0'} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center',}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={close}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UploadBizImportFileDialog;
